import { IonCol, IonRow } from '@ionic/react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AppUserSelf } from 'src/interfaces/AppUserSelf';
import { Location } from 'src/interfaces/Location';
import { License } from 'src/interfaces/License';
import { useApi } from 'src/hooks/useApi';
import { setContactUsCategory, setContactUsOpen } from 'src/redux/appReducer';
import { AlertBanner, AlertVariant } from 'src/components/shared/AlertBanner';

interface LicenseExpirationWarningProps {
    selectedLocation: Location | null;
}

export const LicenseExpirationWarning: React.FC<LicenseExpirationWarningProps> = ({ selectedLocation }) => {
    const api = useApi();
    const dispatch = useDispatch();
    const history = useHistory();
    const authUser: AppUserSelf | null = useSelector((state: any) => state.auth.user);

    const [licensesDates, setLicensesDates] = useState<{ stateExpirationDate?: string, deaExpirationDate?: string } | null>(null);

    const isLicenseExpiringSoon = (expirationDate?: string) => {
        if (!expirationDate) return false;
        const expiration = new Date(expirationDate);
        const today = new Date();
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(today.getDate() + 30);
        return expiration <= thirtyDaysFromNow;
    };

    const isLicenseExpired = (expirationDate?: string) => {
        if (!expirationDate) return false;
        const expiration = new Date(expirationDate);
        const today = new Date();
        return expiration < today;
    };

    const getLicenses = () => {
        api
            .get(`location/${selectedLocation?.location_id}/licenses`)
            .then((response) => {
                const { data } = response;
                if (response.status === 200) {
                    const licenses: License[] = data;
                    const deaLicense = licenses.find(license => license.type === 'dea');
                    const stateLicense = licenses.find(license => license.type === 'state');
                    setLicensesDates({
                        deaExpirationDate: deaLicense?.expiration,
                        stateExpirationDate: stateLicense?.expiration
                    });
                }
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const onClickUpdateLicense = () => {
        if (authUser && authUser.app_role_id < 3) {
            history.push(`/settings/locations/${selectedLocation?.location_id}`);
        } else {
            dispatch(setContactUsOpen(true));
            dispatch(setContactUsCategory('admin'));
        }
    };

    useEffect(() => {
        if (selectedLocation) {
            getLicenses();
        }
    }, [selectedLocation]);

    if (!((licensesDates?.deaExpirationDate && (isLicenseExpiringSoon(licensesDates.deaExpirationDate) || isLicenseExpired(licensesDates.deaExpirationDate))) ||
        (licensesDates?.stateExpirationDate && (isLicenseExpiringSoon(licensesDates.stateExpirationDate) || isLicenseExpired(licensesDates.stateExpirationDate))))) {
        return null;
    }

    const getMessage = () => {
        const messages = [];
        if (licensesDates?.deaExpirationDate && isLicenseExpired(licensesDates.deaExpirationDate)) {
            messages.push(`DEA License expired on ${licensesDates.deaExpirationDate}`);
        }
        if (licensesDates?.stateExpirationDate && isLicenseExpired(licensesDates.stateExpirationDate)) {
            messages.push(`State License expired on ${licensesDates.stateExpirationDate}`);
        }
        if (licensesDates?.deaExpirationDate && !isLicenseExpired(licensesDates.deaExpirationDate) && isLicenseExpiringSoon(licensesDates.deaExpirationDate)) {
            messages.push(`DEA License expires on ${licensesDates.deaExpirationDate}`);
        }
        if (licensesDates?.stateExpirationDate && !isLicenseExpired(licensesDates.stateExpirationDate) && isLicenseExpiringSoon(licensesDates.stateExpirationDate)) {
            messages.push(`State License expires on ${licensesDates.stateExpirationDate}`);
        }
        return messages.map((msg, index) => (
            <>
                {msg}
                {index < messages.length - 1 && <br />}
            </>
        ));
    };

    const getVariant = (): AlertVariant => {
        if ((licensesDates?.deaExpirationDate && isLicenseExpired(licensesDates.deaExpirationDate)) ||
            (licensesDates?.stateExpirationDate && isLicenseExpired(licensesDates.stateExpirationDate))) {
            return 'error';
        }
        return 'warning';
    };

    return (
        <IonRow>
            <IonCol size="12">
                <AlertBanner
                    variant={getVariant()}
                    message={getMessage()}
                    action={{
                        label: 'Update License',
                        onClick: onClickUpdateLicense
                    }}
                />
            </IonCol>
        </IonRow>
    );
}; 